import React, { useState, useMemo } from "react";
import "../Table/Table.css";
import { Button, Tooltip } from "@mui/material";
import DataTable from "../DataTable/DataTable";
import { FiUpload } from "react-icons/fi";
import UploadSOW from "./UploadSOW";
import { BiMessageSquareDetail } from "react-icons/bi";
import SOWDetailsModal from "./SOWDetailsModal";
import { useQuery } from "@tanstack/react-query";
import { extractSow, fetchAllSow, fetchSowById } from "../../api/nttSowApi";
import toast from "react-hot-toast";
import { CgSandClock } from "react-icons/cg";
import ViewButton from "./ViewButton";
import { PiFileCsv } from "react-icons/pi";

function SOWDocument() {
  const [uploadModal, setUploadModal] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedSOW, setSelectedSOW] = useState(null);
  const [loadingId, setLoadingId] = useState(null); 

  const {
    data: sowResponse,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["ntt-sow-list"],
    queryFn: fetchAllSow,
  });

  const contracts = useMemo(() => {
    if (!sowResponse?.data) return [];

    return sowResponse.data.map((sow, index) => ({
      id: index + 1,
      sow_id: sow.sow_id,
      name: sow.file_name || "N/A",
      city: sow.contract_city || "N/A",
      duration: sow.duration_months || 0,
      currency: sow.currency || "N/A",
      technology: sow.technology || "N/A",
      one_time_voice_charges: sow.one_time_voice_charges || 0,
      monthly_charges: sow.monthly_voice_charges || 0,
      annual_charges: sow.annual_charges || 0,
      contract_value: sow.contract_36_month_value || 0,
      verified_contract_36_month_value: sow.verified_contract_36_month_value || 0,
      // date: sow.quote_expiry_date || "N/A",
      sowData: sow,
    }));
  }, [sowResponse]);

  const handleProcessSow = async (sowId) => {
  setLoadingId(sowId);

  try {
    const res = await extractSow(sowId); // call API
    toast.success(res.msg || "SOW processed successfully!");
    refetch(); // refresh list
  } catch (err) {
    console.error(err);
    toast.error(err?.response?.data?.error || "Failed to process SOW.");
  } finally {
    setLoadingId(null);
  }
};

 const handleDownloadCSV = () => {
    if (!contracts || contracts.length === 0) {
      toast.error("No data available to download");
      return;
    }

    // Define CSV headers
    const headers = [
      "SOW ID",
      "File Name",
      "City",
      "Duration (Months)",
      "Currency",
      "Technology",
      "One Time Charges",
      "Monthly Charges",
      "Annual Charges",
      "Contract Value",
      "Verified Contract Value",
      // "Quote Expiry",
      "Status"
    ];

    // Create CSV content
    const csvRows = [];
    csvRows.push(headers.join(","));

    contracts.forEach((contract) => {
      const row = [
        `"${contract.sow_id || ""}"`,
        `"${contract.name || ""}"`,
        `"${contract.city || ""}"`,
        contract.duration || 0,
        `"${contract.currency || ""}"`,
        `"${contract.technology || ""}"`,
        contract.one_time_voice_charges || 0,
        contract.monthly_charges || 0,
        contract.annual_charges || 0,
        contract.contract_value || 0,
        contract.verified_contract_36_month_value || 0,
        // `"${contract.date || ""}"`,
        `"${contract.sowData?.status || "N/A"}"`
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `SOW_Data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("CSV downloaded successfully!");
  };

  const columns = [
    { field: "sow_id", headerName: "SOW ID", width: 150 },
    { field: "name", headerName: "File Name", width: 250 },
    { field: "city", headerName: "City", width: 130 },
    { field: "duration", headerName: "Duration (Months)", width: 170 },
    { field: "currency", headerName: "Currency", width: 100 },
    { field: "technology", headerName: "Technology", width: 130 },
    {
      field: "one_time_voice_charges",
      headerName: "One Time Charges",
      width: 150,
    },
    { field: "monthly_charges", headerName: "Monthly Charges", width: 150 },
    { field: "annual_charges", headerName: "Annual Charges", width: 150 },
    { field: "contract_value", headerName: "Contract Value", width: 150 },
    {
  field: "verified_contract_36_month_value",
  headerName: "Verified Contract Value",
  width: 180,
  renderCell: (params) => {
    const { contract_value, verified_contract_36_month_value } = params.row;
    const mismatch =
      Number(contract_value) !== Number(verified_contract_36_month_value);

    return (
      <div
        style={{
          width: "100%",
          padding: "4px 8px",
          backgroundColor: mismatch ? "#fdecea" : "transparent",
          color: mismatch ? "#b71c1c" : "inherit",
          fontWeight: mismatch ? 600 : 400,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {verified_contract_36_month_value}
        {mismatch && <span title="Mismatch">⚠️</span>}
      </div>
    );
  },
},
    // { field: "verified_contract_36_month_value", headerName: "Verified Contract Value", width: 150 },
    // { field: "date", headerName: "Quote Expiry", width: 130 },
{
  field: "actions",
  headerName: "Actions",
  width: 350,
  renderCell: (params) => {
    const sowRow = params.row.sowData;
    const isLoadingRow = loadingId === sowRow.id;

    const buttonCommonStyles = {
      fontSize: 14,
      width: "160px",
      height: "36px",
      textTransform: "none",
    };

    return (
      <div style={{ display: "flex", gap: "5px",alignItems: "center", marginTop:"8px" }}>
        {sowRow.status === "pending" ? (
          <Button
            variant="contained"
            onClick={() => handleProcessSow(sowRow.id)}
            disabled={isLoadingRow}
            sx={{ ...buttonCommonStyles, backgroundColor: "rgb(34, 150, 34)" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <CgSandClock color="#FFF" size={20} />
              {isLoadingRow ? "Processing..." : "PROCESS"}
            </div>
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => handleViewDetails(sowRow.id)}
            disabled={isLoadingRow}
            sx={{ ...buttonCommonStyles }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              {isLoadingRow ? (
                <>
                  <span
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(255,255,255,0.6)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 0.7s linear infinite",
                    }}
                  ></span>
                  Loading…
                </>
              ) : (
                <>
                  <BiMessageSquareDetail color="#FFF" size={18} />
                  DETAILS
                </>
              )}
            </div>
          </Button>
        )}

        {/* View SOW Button */}
        <ViewButton filePath={sowRow.file_path} />
      </div>
    );
  },
}

  ];

const handleViewDetails = async (sowId) => {
  setLoadingId(sowId);

  try {
    const freshData = await fetchSowById(sowId);
    setSelectedSOW(freshData?.data || null);
    setDetailsModal(true);
  } catch (err) {
    toast.error(err?.response?.data?.error || "Failed. Try again.");
    console.error("Failed to fetch sow by id:", err);
  } finally {
    setLoadingId(null);
  }
};

  return (
    <div >
      <div
        className="table-header"
        style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}
      >
        <h2>Statement of Work (SOW)</h2>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <Tooltip title="Download CSV" placement="top" arrow>
            <span
              onClick={handleDownloadCSV}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isLoading || contracts.length === 0 ? "not-allowed" : "pointer",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                backgroundColor: "#f5f5f5",
                transition: "all 0.2s ease",
                opacity: isLoading || contracts.length === 0 ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoading && contracts.length > 0) {
                  e.currentTarget.style.backgroundColor = "#e0e0e0";
                }
              }}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
            >
              <PiFileCsv size={20} color="#333" />
            </span>
          </Tooltip>

        <Tooltip title="Upload SOW" placement="top" arrow>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              backgroundColor: "#f5f5f5",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
            onClick={() => setUploadModal(true)}
          >
            <FiUpload size={20} color="#333" />
          </span>
        </Tooltip>
      </div>
      </div>

      {/* LOADING STATE */}
      {isLoading && <p>Loading SOW data...</p>}

      {/* ERROR STATE */}
      {isError && <p style={{ color: "red" }}>Failed to load SOW data</p>}

      {/* TABLE */}
      {!isLoading && !isError && (
        <div style={{ marginBottom: "30px" }}>
          <DataTable columns={columns} rows={contracts} />
        </div>
      )}

      {/* MODALS */}
      <UploadSOW open={uploadModal} setOpen={setUploadModal} refetch={refetch} />
      <SOWDetailsModal
        open={detailsModal}
        handleClose={() => setDetailsModal(false)}
        sowData={selectedSOW}
      />
    </div>
  );
}

const profileStyle = {
  width: "90%",
  background: "#fff",
  borderRadius: "8px",
  marginInline: "auto",
  marginTop: "20px",
  padding: "20px",
};

export default SOWDocument;
